/**
 * Vincent B1027 (Eric 2026-02-26 SPEC STRICK PROD) — Bandeau explicatif
 * (« onboarding léger ») affiché en tête de la File d'attente lors du
 * PREMIER accès de l'utilisateur (par device), pour expliquer en
 * 2 lignes les 3 nouveaux verbes introduits par B1025/B1026/B1028 :
 * « Reprendre / Retirer du pipeline / Effacer ».
 *
 * Persistance : localStorage (`queueBriefingDismissed_v1`). Volontairement
 * par device (pas en DB) — le bandeau est purement pédagogique, il n'a
 * pas besoin de suivre l'utilisateur entre ses appareils.
 *
 * Note versioning : la clé est suffixée `_v1`. Si Eric ajoute d'autres
 * verbes plus tard, il suffira de bumper le suffixe pour re-afficher.
 */
import { useEffect, useState } from "react";
import { Info, X } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";


const STORAGE_KEY = "queueBriefingDismissed_v1";


export default function QueueBriefing() {
  // Tâche 1378 (Eric 2026-06-30) — Bandeau resté français.
  const { tr } = useLang();
  const [dismissed, setDismissed] = useState(true);

  // Lecture initiale au montage (côté client uniquement — SSR-safe).
  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      // localStorage indisponible (mode privé Safari, etc.) → toujours afficher.
      setDismissed(false);
    }
  }, []);

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // On ferme quand même côté UI.
    }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="queue-briefing-banner"
      className="mb-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 flex items-start gap-3"
    >
      <Info size={20} weight="fill" className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-sm text-blue-900 dark:text-blue-200 leading-snug">
        <div className="font-semibold mb-1">{tr("Nouveauté — 3 verbes clairs pour agir sur une vidéo :", "New — 3 clear verbs to act on a video:")}</div>
        <div className="text-xs">
          <span className="font-semibold">{tr("Reprendre", "Resume")}</span>{" "}
          {tr("= relancer ou continuer le pipeline (menu adapté à l'état).", "= relaunch or continue the pipeline (menu adapts to the state).")}{" "}
          <span className="font-semibold">{tr("Retirer du pipeline", "Remove from the pipeline")}</span>{" "}
          {tr("= sortir de la file en gardant toutes les données (réversible).", "= leave the queue while keeping all the data (reversible).")}{" "}
          <span className="font-semibold">{tr("Effacer (vidéo + données)", "Delete (video + data)")}</span>{" "}
          {tr("= suppression définitive et irréversible.", "= permanent, irreversible deletion.")}
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="queue-briefing-dismiss"
        className="shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 p-1"
        aria-label={tr("Masquer ce message", "Hide this message")}
        title={tr("J'ai compris — ne plus afficher", "Understood — stop showing this")}
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
